import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { RippleModule } from 'primeng/ripple';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { Richiesta } from '../../../models/richiesta.model';
import { NGXLogger } from 'ngx-logger';
import { RichiesteService } from '../../../services/richieste.service';
import { ToastModule } from 'primeng/toast';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-comunica-contributo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    TooltipModule,
    CardModule,
    DividerModule,
    RippleModule,
    ToastModule,
    FileUploadModule,
    ProgressSpinnerModule
  ],
  providers: [MessageService],
  templateUrl: './comunica-contributo.component.html',
  styleUrl: './comunica-contributo.component.css'
})
export class ComunicaContributoComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() richiesta: Richiesta | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confermato    = new EventEmitter<{ richiestaId: number, importoContributo: number, allegato?: File }>();

  contributoForm!: FormGroup;
  mostraErroreImporto: boolean = false;
  allegatoPDF: File | null = null;
  labelPulsante: string = 'Cerca file PDF';
  classPulsante: string = 'btn-choose-pdf';
  iconaPulsante: string = 'pi pi-file-pdf';
  isProcessing: boolean = false;

  // Proprietà per determinare se è una modifica
  get isModifica(): boolean {
    return !!(this.richiesta?.importoContributo && this.richiesta.importoContributo > 0);
  }

  constructor(
    private fb: FormBuilder,
    private logger: NGXLogger,
    private richiesteService: RichiesteService,
    private messageService: MessageService,
    private firebaseStorageService: FirebaseStorageService
  ) {}

  ngOnInit(): void {
    //this.logger.info(`[COMUNICA-CONTRIBUTO] INIT: ${JSON.stringify(this.richiesta)}`);
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      //this.logger.info(`[COMUNICA-CONTRIBUTO] ON CHANGES: ${JSON.stringify(this.richiesta)}`);
      this.resetForm();
    }
  }

  // Validatore personalizzato per controllare che l'importo contributo non superi l'importo richiesto
  importoMaxValidator(richiesta: Richiesta | null): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !richiesta || !richiesta.importoRichiesto) {
        return null;
      }

      return control.value > richiesta.importoRichiesto
        ? { importoEccessivo: { value: control.value, max: richiesta.importoRichiesto } }
        : null;
    };
  }

  initForm(): void {
    ////this.logger.info(`[COMUNICA-CONTRIBUTO] Richiesta: ${JSON.stringify(this.richiesta)}`);
    const formControls: any = {
      importoContributo: [
        this.richiesta?.importoContributo ? this.richiesta.importoContributo : null,
        [
          Validators.required,
          Validators.min(1),

        ]
      ]
    };

    // Se è una modifica, aggiungi il campo note invece dell'allegato
    if (this.isModifica) {
      formControls.note = [null];
    } else {
      formControls.allegato = [null];
    }

    this.contributoForm = this.fb.group(formControls);

    // Monitoraggio delle modifiche al campo importoContributo
    this.contributoForm.get('importoContributo')?.valueChanges.subscribe(value => {
      // Reset dell'errore quando si modifica il valore
      this.mostraErroreImporto = false;
      
      // Non mostrare messaggi/controlli se è una modifica
      if (!this.isModifica && this.richiesta && this.contributoForm.get('importoContributo')?.value > 0 &&
          this.contributoForm.get('importoContributo')?.value > this.richiesta.importoRichiesto) {
            const contributo = this.formattaImporto(this.contributoForm.get('importoContributo')?.value);
            const max = this.formattaImporto(this.richiesta.importoRichiesto);
            this.messageService.add({
              key: 'importoEccessivo',
              severity: 'warning',
              summary: 'Contributo superiore all\'importo richiesto',
              detail: `ATTENZIONE: L'importo del contributo (${contributo} €) è superiore all'importo richiesto (${max} €). Siete sicuri di voler procedere?`,
              life: 2000,
              styleClass: 'warning-toast'
            });
      }

      // Verifica se il valore attuale supera l'importo richiesto
      /* if (this.contributoForm.get('importoContributo')?.hasError('importoEccessivo') &&
          this.richiesta &&
          value > 0) {

        // Se l'utente sta ancora digitando, mostriamo il toast solo dopo un breve ritardo
        // per evitare di mostrarlo ad ogni singolo carattere digitato
        setTimeout(() => {
          if (this.contributoForm.get('importoContributo')?.hasError('importoEccessivo')) {
            const max = this.formattaImporto(this.richiesta!.importoRichiesto);
            const contributo = this.formattaImporto(value);

            this.messageService.clear('importoEccessivo');
            this.messageService.add({
              key: 'importoEccessivo',
              severity: 'error',
              summary: 'Importo non valido',
              detail: `L'importo del contributo (${contributo} €) non può superare l'importo richiesto (${max} €)`,
              life: 3000,
              styleClass: 'error-toast'
            });
          }
        }, 300);
      } */
    });
  }

  resetForm(): void {
    if (this.contributoForm) {
      this.contributoForm.reset();
      this.mostraErroreImporto = false;
      this.initForm();
    }
  }

  hideDialog(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.reset();
  }

  confermaContributo(): void {
    if (this.contributoForm.valid && this.richiesta) {
      this.isProcessing = true;
      const importoContributo = this.contributoForm.get('importoContributo')?.value;

      // Se è una modifica, usa l'API di variazione contributo
      if (this.isModifica) {
        const note = this.contributoForm.get('note')?.value || '';
        this.richiesteService.variazioneContributo(this.richiesta.id, importoContributo, note).subscribe({
          next: () => {
            this.confermato.emit({
              richiestaId: this.richiesta!.id,
              importoContributo: importoContributo
            });
            this.isProcessing = false;
            this.reset();
            this.hideDialog();
          },
          error: (error: any) => {
            this.logger.error(`[COMUNICA-CONTRIBUTO] Errore durante la variazione del contributo: ${error}`);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Errore durante la modifica del contributo'
            });
            this.isProcessing = false;
          }
        });
        return;
      }

      // Se non è una modifica, procedi con la logica originale
      let richiestaDaModificare = this.richiesta;
      richiestaDaModificare.importoContributo = importoContributo;
      this.logger.info(`[COMUNICA-CONTRIBUTO] Richiesta da modificare: ${JSON.stringify(richiestaDaModificare)}`);
      // Se c'è un allegato PDF, lo carico prima su Firestore
      if (this.allegatoPDF) {
        const path = `richieste/${richiestaDaModificare.id}/allegati/${this.allegatoPDF.name}`;

        this.firebaseStorageService.uploadFile(this.allegatoPDF, path).subscribe({
          next: (url) => {
            richiestaDaModificare.allegatoPdf = true;
            // Dopo il caricamento del file, aggiorno la richiesta
            this.aggiornaRichiesta(richiestaDaModificare, importoContributo);
          },
          error: (error: Error) => {
            this.logger.error(`[COMUNICA-CONTRIBUTO] Errore durante il caricamento del file:`, error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Errore durante il caricamento del file'
            });
            this.isProcessing = false;
            this.hideDialog();
          }
        });
      } else {
        // Se non c'è allegato, procedo direttamente con l'aggiornamento della richiesta
        this.aggiornaRichiesta(richiestaDaModificare, importoContributo);
      }
    } else if (!this.isModifica && this.contributoForm.get('importoContributo')?.hasError('importoEccessivo') && this.richiesta) {
      // Mostra messaggio di errore solo se NON è una modifica
      this.mostraErroreImporto = true;
      const max = this.formattaImporto(this.richiesta.importoRichiesto);
      const contributo = this.formattaImporto(this.contributoForm.get('importoContributo')?.value);

      this.messageService.clear();
      this.messageService.add({
        key: 'importoEccessivo',
        severity: 'warn',
        summary: 'Contributo superiore all\'importo richiesto',
        detail: `ATTENZIONE: L'importo del contributo (${contributo} €) è superiore all'importo richiesto (${max} €)`,
        life: 5000
      });
    }
  }

  private aggiornaRichiesta(richiesta: Richiesta, importoContributo: number): void {
    this.richiesteService.comunicaContributoRichiesta(richiesta).subscribe({
      next: () => {
        this.confermato.emit({
          richiestaId: richiesta.id,
          importoContributo: importoContributo,
          allegato: this.allegatoPDF || undefined
        });
        this.isProcessing = false;
        this.reset();
        this.hideDialog();
      },
      error: (error: any) => {
        this.logger.error(`[COMUNICA-CONTRIBUTO] Errore durante la modifica della richiesta: ${error}`);
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Errore durante la modifica della richiesta' });
        this.isProcessing = false;
        this.reset();
        this.hideDialog();
      }
    });
  }

  formattaImporto(importo: number | null): string {
    if (importo === null) return '-';
    return new Intl.NumberFormat('it-IT').format(importo);
  }

  onPDFSelect(event: any): void {
    this.labelPulsante  = 'File PDF caricato';
    this.classPulsante  = "btn-pdf-upload-success";

  }
  onPDFUpload(event: any): void {
    const file = event.files[0];
    if (file && file.type === 'application/pdf') {
      this.allegatoPDF = file;
      this.messageService.add({
        severity: 'success',
        summary: 'File caricato',
        detail: 'Il file PDF è stato caricato con successo'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Per favore, carica solo file PDF'
      });
    }
  }

  onPDFRemove(): void {
    this.allegatoPDF = null;
  }

  reset(): void {
    // Reset del form
    if (this.contributoForm) {
      this.contributoForm.reset();
      this.contributoForm.markAsPristine();
      this.contributoForm.markAsUntouched();
    }

    // Reset delle variabili di stato
    this.mostraErroreImporto = false;
    this.isProcessing = false;
    this.richiesta = null;
    this.allegatoPDF = null;

    // Reset delle variabili del file upload
    this.labelPulsante = 'Cerca file PDF';
    this.classPulsante = 'btn-choose-pdf';
    this.iconaPulsante = 'pi pi-file-pdf';

    // Reinizializzazione del form
    this.initForm();
  }
}
